import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import {
  useApolloClient, useQuery, useMutation,
} from '@apollo/client'
import _get from 'lodash/get'
import type { Dispatch, UnknownAction } from 'redux'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../../apollo-graphql/index'
import {
  GET_FOLLOWED_PLAYLISTS_SHELL,
  UNFOLLOW_PLAYLIST,
} from '../../query'
import showSaveErrorToast from '../../shared/save-error-toast'
import getText from '../../shared/static-text-utils'
import { createRemoveItemUpdater } from '../../shared/cache-updaters'

import type {
  GetFollowedPlaylistsShellResponse,
  FollowedPlaylistShellNode,
  StaticTextMap,
} from '../../types'

// Edge type derived from the generated response — used for the pages-2+
// local-state accumulator (see hook body for rationale).
type FollowedPlaylistEdge = NonNullable<
  GetFollowedPlaylistsShellResponse['followedPlaylists']
>['edges'][number]

// Module-scope empty sentinel so every pre-data memo returns the same
// reference.  Prevents downstream re-renders.
const EMPTY_EDGES: FollowedPlaylistEdge[] = []

interface UseFollowedPlaylistsParams {
  uid: string
  language: string
  jwt: string
  isLoggedIn: boolean
  dispatch: Dispatch<UnknownAction>
  staticText: StaticTextMap
  /** When false, skips followed-playlist queries (e.g. community feature gate will 404). */
  queriesEnabled?: boolean
  /**
   * Server-side substring filter applied to playlist name / author display
   * name. Empty string (or omitted) means "no filter" — `searchQuery || undefined`
   * is forwarded so Apollo's cache key for the filtered variant stays distinct
   * from the unfiltered cache key while remaining a no-op on the server.
   */
  searchQuery?: string
}

export default function useFollowedPlaylists (params: UseFollowedPlaylistsParams) {
  // `language` stays on the param contract for callers (useCollectionsData
  // still threads it through) but is no longer consumed here — per-row
  // hydration in FollowedPlaylistRow owns the GET_CONTENT call site.
  const {
    uid, jwt, isLoggedIn, dispatch, staticText, queriesEnabled = true, searchQuery = '',
  } = params
  const saveErrorMessage = getText(staticText, 'saveError', '')
  const apolloClient = useApolloClient()

  const SHELL_PAGE_SIZE = 24

  // cache-and-network: user-scoped shell — playlistItems carry only IDs.
  // Tile content hydrates via the cache-and-network GET_CONTENT call in
  // useRowHydration on each row.
  //
  // Cache paints the previous followed-playlists list instantly when the
  // user returns to /playlist/default; the background round-trip then
  // reconciles with the server.  Safety:
  //   • handleRemoveFollowedPlaylist below uses optimistic cache.modify via
  //     createRemoveItemUpdater, so unfollows stay correct under a cached
  //     shell.  Refollow / failure cases still call refetchFollowedPlaylists.
  //   • Logout / profile-switch wipe the cache via global.location.assign,
  //     so no user-data leak on session change.

  const {
    data: followedPlaylistsData,
    loading: shellLoading,
    refetch: refetchFollowedPlaylists,
  } = useQuery<GetFollowedPlaylistsShellResponse>(GET_FOLLOWED_PLAYLISTS_SHELL, {
    variables: { uid, limit: SHELL_PAGE_SIZE, searchQuery: searchQuery || undefined },
    skip: !isLoggedIn || !uid || !queriesEnabled,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt },
  })

  // Cursor pagination for pages 2+ — direct `apolloClient.query` +
  // local React state.  Page 1 keeps `useQuery` for cache hydration,
  // SSR paint, and fast remount.
  //
  // ──────────────────────────────────────────────────────────────────
  // TWIN-CODE — keep in sync (see follow-up ticket for shared extraction):
  //   • `Community/Identity/Profile/ProfilePlaylistsSection/`
  //     `ProfilePlaylistsSection.tsx` — offset variant (uses
  //     `paginationOffset` instead of `paginationCursor`)
  //   • `Community/Identity/shared/ConnectionsModal/ConnectionsModal.tsx`
  //     — two cursor instances (followers + following)
  // All three share: page-1 useQuery + pages-2+ no-cache query + local
  // extras accumulator + reset-on-key-change effect + reqGenRef.  When
  // editing any pagination logic here, audit those siblings too.
  // ──────────────────────────────────────────────────────────────────
  //
  // Why not `useApolloListPagination` / `fetchMore`?
  //   Apollo's `fetchMore` returns the *watched query's data after the
  //   cache merge has run*, not the raw network response.  When the
  //   `relayStylePagination` merge doesn't fire — because no `updateQuery`
  //   was passed AND the typePolicy can't locate the cursor in existing
  //   edges, or for whatever 3.14.0-internal reason — the watched-query
  //   cache slot doesn't change.  `fetchMore`'s `result.data` then mirrors
  //   that unchanged-or-absent state and comes back `undefined`.
  //
  //   A temporary `[Following][fetchMore-result-shape]` diagnostic (now
  //   stripped) confirmed this across 30+ scrolls during triage:
  //   `hasData: false, dataKeys: null` every time.  Three prior attempts
  //   (useApolloListPagination + updateQuery; bare relayStylePagination
  //   typePolicy; manual writeQuery after fetchMore) each fought the
  //   symptom and produced the same infinite-IO loop.
  //
  // The fix: bypass `fetchMore` entirely.  `apolloClient.query` with
  // `fetchPolicy: 'no-cache'` returns the raw network response untouched
  // by the cache-merge pipeline, and we accumulate pages locally.
  //
  // Plan: .claude-work/plans/system-role-you-are-groovy-elephant.md
  // State resets on unmount by design — page 1 rehydrates from the Apollo
  // cache (useQuery is `cache-and-network`) and pages 2+ refetch.  The
  // alternative — surviving unmount via a context/redux mirror — was
  // explicitly rejected when we ditched `fetchMore`; see the docblock above.
  const [extraEdges, setExtraEdges] = useState<FollowedPlaylistEdge[]>([])
  const [paginationCursor, setPaginationCursor] = useState<string | null>(null)
  const [paginationHasMore, setPaginationHasMore] = useState<boolean>(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Request-generation counter.  Bumped inside the reset effect; `loadMore`
  // captures the value at fire time and bails on resolve if it no longer
  // matches.  Prevents an in-flight loadMore (under uid A / filter A) from
  // appending stale rows after a reset triggered by a uid or searchQuery flip.
  const reqGenRef = useRef<number>(0)
  // Synchronous in-flight latch — LoadMore's IntersectionObserver can fire
  // twice before React commits `setIsLoadingMore(true)`.
  const loadMoreInFlightRef = useRef<boolean>(false)

  // Reset pagination state when uid changes (account switch / logout) or when
  // searchQuery flips. The page-1 useQuery auto-refetches on its own when its
  // variable changes; this effect clears the page-2+ accumulator so the new
  // filtered set isn't appended to stale unfiltered (or differently-filtered) rows.
  useEffect(() => {
    reqGenRef.current += 1
    setExtraEdges([])
    setPaginationCursor(null)
    setPaginationHasMore(false)
  }, [uid, searchQuery])

  // Seed cursor + hasMore from page 1 once it lands.  Guarded by
  // `extraEdges.length === 0` so a subsequent page-1 refetch doesn't
  // clobber the cursor we've already advanced.
  useEffect(() => {
    const p1 = followedPlaylistsData?.followedPlaylists
    if (!p1 || extraEdges.length > 0) return
    setPaginationCursor(p1.pageInfo?.endCursor ?? null)
    setPaginationHasMore(!!p1.pageInfo?.hasNextPage)
  }, [followedPlaylistsData, extraEdges.length])

  const loadMore = useCallback(async (): Promise<void> => {
    if (loadMoreInFlightRef.current || !paginationHasMore || !paginationCursor) return
    loadMoreInFlightRef.current = true

    const myGen = reqGenRef.current
    setIsLoadingMore(true)
    try {
      const result = await apolloClient.query<GetFollowedPlaylistsShellResponse>({
        query: GET_FOLLOWED_PLAYLISTS_SHELL,
        variables: {
          uid,
          limit: SHELL_PAGE_SIZE,
          after: paginationCursor,
          searchQuery: searchQuery || undefined,
        },
        fetchPolicy: 'no-cache',
        errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
        context: { jwt },
      })

      if (myGen !== reqGenRef.current) return

      const nextPage = result.data?.followedPlaylists
      const newEdges = (nextPage?.edges ?? []) as FollowedPlaylistEdge[]

      if (newEdges.length > 0) {
        setExtraEdges((prev) => [...prev, ...newEdges])
      }

      setPaginationCursor(nextPage?.pageInfo?.endCursor ?? null)
      setPaginationHasMore(!!nextPage?.pageInfo?.hasNextPage)
    } finally {
      loadMoreInFlightRef.current = false
      setIsLoadingMore(false)
    }
    // `apolloClient` is a stable singleton from `useApolloClient()` — listed
    // for exhaustive-deps but never identity-flips in practice.
  }, [apolloClient, uid, jwt, paginationCursor, paginationHasMore, searchQuery])

  const [unfollowPlaylistMutation] = useMutation(UNFOLLOW_PLAYLIST, {
    context: { jwt },
  })

  // Combined edges: page 1 (Apollo cache) + pages 2+ (local state).
  const allEdges: FollowedPlaylistEdge[] = useMemo(() => {
    const p1Edges = (_get(followedPlaylistsData, 'followedPlaylists.edges', EMPTY_EDGES)
      || EMPTY_EDGES) as FollowedPlaylistEdge[]
    return [...p1Edges, ...extraEdges]
  }, [followedPlaylistsData, extraEdges])

  const followedPlaylistsJS: FollowedPlaylistShellNode[] = useMemo(() => allEdges
    .map((edge) => edge.node)
    .filter((n): n is FollowedPlaylistShellNode => Boolean(n)), [allEdges])

  const followedPlaylistsTotalCount = _get(
    followedPlaylistsData,
    'followedPlaylists.totalCount',
    null,
  )

  // Guard async callbacks against state updates after unmount.
  const isMountedRef = useRef(true)
  useEffect(() => () => { isMountedRef.current = false }, [])

  const handleRemoveFollowedPlaylist = useCallback(async (playlistId: string) => {
    // Surgical cache removal — fires synchronously so the row disappears
    // on the next render without a server round-trip.  Mirrors PR 5's
    // cache.modify pattern for REMOVE_PLAYLIST_ITEM on My Playlists.
    //
    // `followedPlaylists` is a root-Query connection field keyed by its
    // variables (uid + limit + after), so Apollo invokes this modifier
    // once per stored page.  Pages that don't contain `playlistId`
    // return the existing reference unchanged — their cache entry's
    // identity is preserved.  Apollo stores edges as normalized refs
    // (`{ __ref: 'Playlist:<id>' }`), so we must use `readField('id',
    // edge.node)` rather than `edge.node.id` (the latter returns
    // undefined on a Reference).
    // Surgical cache removal via the shared factory. Filters the connection's
    // edges by matching edge.node.id, then decrements totalCount in the same
    // pass — keeps the (count, edges) pair consistent.
    const updater = createRemoveItemUpdater({
      entityId: 'ROOT_QUERY',
      field: 'followedPlaylists',
      edgesPath: 'edges',
      countField: 'totalCount',
      countLocation: 'within-connection',
      match: (ref, readField) => readField('id', ref) === playlistId,
    })
    updater(apolloClient.cache)

    // Filter local extras too — pages 2+ aren't in the cache, so the
    // `updater` above only touches page 1.
    setExtraEdges((prev) => prev.filter((edge) => {
      const node = edge.node as { id?: string } | undefined
      return node?.id !== playlistId
    }))

    try {
      await unfollowPlaylistMutation({ variables: { playlistId } })
      // Success → cache + extras already reflect the removal.
    } catch {
      if (!isMountedRef.current) return
      // Mutation failed — reset extras and refetch page 1 to restore
      // the row that we removed optimistically.
      setExtraEdges([])
      setPaginationCursor(null)
      setPaginationHasMore(false)
      refetchFollowedPlaylists()
      showSaveErrorToast(dispatch, saveErrorMessage)
    }
  }, [
    apolloClient, unfollowPlaylistMutation,
    refetchFollowedPlaylists, dispatch, saveErrorMessage,
  ])

  const handleReFollowPlaylist = useCallback(async (): Promise<void> => {
    refetchFollowedPlaylists()
  }, [refetchFollowedPlaylists])

  return {
    followedPlaylistsJS,
    followedPlaylistsLoading: shellLoading && !followedPlaylistsData,
    followedPlaylistsTotalCount,
    refetchFollowedPlaylists,
    loadMoreFollowedPlaylists: loadMore,
    hasMoreFollowedPlaylists: paginationHasMore,
    isLoadingMoreFollowedPlaylists: isLoadingMore,
    handleRemoveFollowedPlaylist,
    handleReFollowPlaylist,
  }
}
